import { forwardRef, useImperativeHandle, useLayoutEffect, useRef } from 'react';
import type { KeyboardEvent } from 'react';
import { getEmojiPack } from './emojiSets';
import { codepointsToUnicode, parseEmojiPlaceholders } from './placeholder';

export type EmojiComposerHandle = {
  focus: () => void;
  insertEmoji: (placeholder: string) => void;
};

type EmojiComposerProps = {
  value: string;
  onChange: (value: string) => void;
  onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
  placeholder: string;
  disabled: boolean;
};

export const EmojiComposer = forwardRef<EmojiComposerHandle, EmojiComposerProps>(
  function EmojiComposer({ value, onChange, onKeyDown, placeholder, disabled }, ref) {
    const editorRef = useRef<HTMLDivElement | null>(null);
    const valueRef = useRef(value);
    valueRef.current = value;

    useImperativeHandle(ref, () => ({
      focus: () => {
        editorRef.current?.focus();
      },
      insertEmoji: (placeholderValue: string) => {
        const editor = editorRef.current;
        if (!editor || disabled) return;
        editor.focus();
        insertNodeAtSelection(editor, createEmojiNode(placeholderValue));
        const next = serializeEditor(editor);
        valueRef.current = next;
        onChange(next);
      },
    }), [disabled, onChange]);

    useLayoutEffect(() => {
      const editor = editorRef.current;
      if (!editor) return;
      if (serializeEditor(editor) === value) return;
      renderEditorValue(editor, value);
    }, [value]);

    return (
      <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
        <div
          ref={editorRef}
          role="textbox"
          aria-multiline="true"
          aria-label="输入消息"
          contentEditable={!disabled}
          suppressContentEditableWarning
          onInput={(event) => {
            const next = serializeEditor(event.currentTarget);
            valueRef.current = next;
            onChange(next);
          }}
          onPaste={(event) => {
            event.preventDefault();
            const text = event.clipboardData.getData('text/plain');
            if (!text) return;
            insertTextAtSelection(event.currentTarget, text);
            const next = serializeEditor(event.currentTarget);
            valueRef.current = next;
            onChange(next);
          }}
          onKeyDown={(event) => {
            onKeyDown(event);
            if (event.defaultPrevented) return;
            if (event.key === 'Enter' && event.shiftKey) {
              event.preventDefault();
              insertTextAtSelection(event.currentTarget, '\n');
              const next = serializeEditor(event.currentTarget);
              valueRef.current = next;
              onChange(next);
            }
          }}
          style={{
            minHeight: 46,
            maxHeight: 160,
            overflowY: 'auto',
            outline: 'none',
            padding: '12px 14px',
            fontSize: 15,
            lineHeight: 1.4,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            cursor: disabled ? 'not-allowed' : 'text',
            opacity: disabled ? 0.62 : 1,
          }}
        />
        {!value && (
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: 14,
              top: 12,
              color: 'var(--mantine-color-placeholder)',
              pointerEvents: 'none',
              fontSize: 15,
              lineHeight: 1.4,
            }}
          >
            {placeholder}
          </span>
        )}
      </div>
    );
  },
);

function renderEditorValue(editor: HTMLDivElement, value: string) {
  editor.replaceChildren();
  for (const segment of parseEmojiPlaceholders(value)) {
    if (segment.type === 'text') {
      editor.append(document.createTextNode(segment.value));
      continue;
    }
    editor.append(createEmojiNode(segment.raw));
  }
}

function createEmojiNode(placeholder: string) {
  const [segment] = parseEmojiPlaceholders(placeholder);
  if (segment?.type !== 'emoji') {
    return document.createTextNode(placeholder);
  }

  const pack = getEmojiPack(segment.pack);
  const url = pack.resolveUrl(segment.codepoints);
  const img = document.createElement('img');
  img.dataset.emojiPlaceholder = segment.raw;
  img.src = url ?? '';
  img.alt = codepointsToUnicode(segment.codepoints);
  img.contentEditable = 'false';
  img.draggable = false;
  img.style.display = 'inline-block';
  img.style.width = '1.22em';
  img.style.height = '1.22em';
  img.style.verticalAlign = '-0.24em';
  img.style.margin = '0 1px';
  return img;
}

function insertNodeAtSelection(editor: HTMLDivElement, node: Node) {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0 || !editor.contains(selection.anchorNode)) {
    editor.append(node);
    editor.append(document.createTextNode(''));
    placeCaretAtEnd(editor);
    return;
  }

  const range = selection.getRangeAt(0);
  range.deleteContents();
  range.insertNode(node);
  range.setStartAfter(node);
  range.collapse(true);
  selection.removeAllRanges();
  selection.addRange(range);
}

function insertTextAtSelection(editor: HTMLDivElement, text: string) {
  insertNodeAtSelection(editor, document.createTextNode(text));
}

function placeCaretAtEnd(editor: HTMLDivElement) {
  const range = document.createRange();
  const selection = window.getSelection();
  range.selectNodeContents(editor);
  range.collapse(false);
  selection?.removeAllRanges();
  selection?.addRange(range);
}

function serializeEditor(editor: HTMLElement) {
  return Array.from(editor.childNodes).map(serializeNode).join('');
}

function serializeNode(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent ?? '';
  }
  if (!(node instanceof HTMLElement)) {
    return '';
  }
  const placeholder = node.dataset.emojiPlaceholder;
  if (placeholder) {
    return placeholder;
  }
  if (node.tagName === 'BR') {
    return '\n';
  }
  return Array.from(node.childNodes).map(serializeNode).join('');
}
